import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Chart as ChartJS } from "chart.js";
import { chartColors } from "../config/theme";

// One switch for the whole app. The palette lives in CSS variables (src/index.css)
// and Tailwind reads them, so toggling `dark` on <html> restyles every page; DaisyUI
// components follow via data-theme; canvases (Chart.js, MapLibre, deck.gl) take colours from useThemeColors(), which reads isDark here.

export const STORAGE_KEY = "telcofy:theme"; // "light" | "dark"; absent = follow the OS

const systemPrefersDark = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;

/** Initial mode: the saved choice, else the OS setting. Safe to call before render. */
export function readInitialDark(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "dark") return true;
    if (v === "light") return false;
  } catch {
    /* storage unavailable — fall through to the OS setting */
  }
  return systemPrefersDark();
}

/** Apply to the document. Called once pre-render (no flash) and on every toggle. */
export function applyTheme(dark: boolean) {
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  root.dataset.theme = dark ? "mydark" : "mytheme";
  const c = chartColors(dark);
  ChartJS.defaults.color = c.tick;
  ChartJS.defaults.borderColor = c.grid;
}

const ThemeContext = createContext<{ isDark: boolean; toggle: () => void }>({
  isDark: false,
  toggle: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(readInitialDark);

  // Layout effect: `html.dark` and the Chart.js defaults flip before paint, and before
  // react-chartjs-2's passive update reads them.
  useLayoutEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  // Follow the OS while the user hasn't chosen explicitly.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => {
      try {
        if (localStorage.getItem(STORAGE_KEY)) return;
      } catch {
        /* ignore */
      }
      setIsDark(e.matches);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setIsDark((d) => {
      const next = !d;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "dark" : "light");
      } catch {
        /* still toggles for this session */
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ isDark, toggle }), [isDark, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
